import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession, useToast } from '../lib/store';
import type { AppNotification } from '../lib/types';
import { timeAgo } from '../lib/format';

/**
 * The notification bell.
 *
 * The API for this has existed since the beginning (`GET /me/notifications`,
 * `POST /me/notifications/read`) and `me.unreadNotifications` was already on
 * the shell payload — there was simply never a way to see any of it. Everything
 * the game tells you about (someone fed your Pokemon, an egg is ready, a
 * Living Trainer visited, a chat mention) landed in a table nobody could open.
 *
 * Deliberately *not* marking everything read on open. Opening the panel to
 * glance at one line should not silently discard the other nine — each row is
 * marked as you act on it, and there are two explicit bulk controls.
 *
 * **Mark all read** is one server statement covering every unread row this
 * player has, not just the fifty on screen. That distinction is the whole
 * feature: a player back from a week away has a backlog the list cannot even
 * show, and a button that cleared only the visible page would leave a badge
 * that refuses to reach zero.
 *
 * **Clear read** then removes rows already seen, because the list is capped at
 * the newest fifty — without it, reading everything still leaves a panel of
 * fifty rows of history with no way to tell what is new. It cannot touch an
 * unread row; that filter is on the server, not here.
 */

const TYPE_ART: Record<string, string> = {
  EGG_READY: '◔',
  HATCHED: '◓',
  FED: '♥',
  LEVEL_UP: '★',
  ACHIEVEMENT: '★',
  EVOLUTION_READY: '⟳',
  DAILY: '◆',
  NPC_VISIT: '🌿',
  CHAT_MENTION: '✎',
  GUILD_ANNOUNCEMENT: '🏰',
  SYSTEM: '◈',
};

/** Where a notification should take you, from the payload it carries. */
function destinationOf(row: AppNotification): string | null {
  const data = row.data ?? {};
  if (typeof data.pokemonId === 'string') return `/pokemon/${data.pokemonId}`;
  if (typeof data.eggId === 'string') return '/hatchery';
  if (typeof data.guildKey === 'string') return `/guilds/${data.guildKey}`;
  if (row.type === 'ACHIEVEMENT') return '/achievements';
  if (row.type === 'DAILY') return '/';
  return null;
}

export function NotificationBell() {
  const { me, refresh } = useSession();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const unread = me?.unreadNotifications ?? 0;

  // Loaded on open rather than on mount: the badge count already rides on the
  // shell payload, so the list itself is only worth fetching when someone asks
  // to see it.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get<{ notifications: AppNotification[] }>('/me/notifications')
      .then((result) => setRows(result.notifications))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onClick = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  async function markRead(ids?: string[]) {
    // Optimistic: the badge and the rows update now, and `refresh()` reconciles
    // with the server's count a moment later. Clicking a button and waiting a
    // round trip to see anything happen is what made clearing feel tedious.
    setRows((current) =>
      current.map((row) => (!ids || ids.includes(row.id) ? { ...row, read: true } : row)),
    );
    try {
      const result = await api.post<{ unread: number; cleared: number }>(
        '/me/notifications/read',
        ids ? { ids } : {},
      );
      // Said out loud only for the bulk case, and only when it did something.
      // A backlog of two hundred vanishing silently reads as a bug.
      if (!ids && result.cleared > 0) {
        push('success', `Marked ${result.cleared} alert${result.cleared === 1 ? '' : 's'} read.`);
      }
      void refresh();
    } catch {
      push('error', 'Could not update your alerts.');
      void refresh();
    }
  }

  /** Removes rows already seen. The server refuses to touch anything unread. */
  async function clearRead() {
    const readIds = new Set(rows.filter((row) => row.read).map((row) => row.id));
    if (readIds.size === 0) return;

    setRows((current) => current.filter((row) => !readIds.has(row.id)));
    try {
      const result = await api.post<{ dismissed: number }>('/me/notifications/dismiss-read', {});
      if (result.dismissed > 0) {
        push('success', `Cleared ${result.dismissed} read alert${result.dismissed === 1 ? '' : 's'}.`);
      }
    } catch {
      push('error', 'Could not clear those.');
    }
  }

  if (!me) return null;

  return (
    <div className="bell" ref={wrapRef}>
      <button
        type="button"
        className={`bell__button ${open ? 'bell__button--open' : ''}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <span className="bell__icon" aria-hidden>
          ◔
        </span>
        <span className="bell__label">Alerts</span>
        {unread > 0 && (
          <span className="bell__badge" aria-hidden>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="pxframe pxframe--raised bell__panel" role="dialog" aria-label="Notifications">
          <div className="bell__head">
            <span className="mono tiny">
              Alerts{unread > 0 ? ` · ${unread} unread` : ''}
            </span>
            <span className="spacer" />
            {/* Two controls, and only when they would do something. "Mark all
                read" covers every unread row the player has, including ones
                past the fifty this list shows. */}
            {unread > 0 && (
              <button type="button" className="bell__mark" onClick={() => void markRead()}>
                ✓ Mark all read
              </button>
            )}
            {rows.some((row) => row.read) && (
              <button type="button" className="bell__mark" onClick={() => void clearRead()}>
                Clear read
              </button>
            )}
          </div>

          <div className="bell__list">
            {loading ? (
              <div className="bell__empty tiny muted">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="bell__empty tiny muted">
                Nothing yet. Visit a Grove and someone will usually help back.
              </div>
            ) : (
              rows.map((row) => {
                const href = destinationOf(row);
                const body = (
                  <>
                    <span className="bell__art" aria-hidden>
                      {TYPE_ART[row.type] ?? '◈'}
                    </span>
                    <span className="bell__body">
                      <span className="bell__title">{row.title}</span>
                      <span className="tiny muted">{row.body}</span>
                    </span>
                    <span className="tiny muted bell__when">{timeAgo(row.createdAt)}</span>
                  </>
                );

                const className = `bell__row ${row.read ? '' : 'bell__row--unread'}`;

                // A row that leads somewhere is a link; one that does not is a
                // button that just clears itself. Both mark themselves read, so
                // acting on a notification is what dismisses it.
                return href ? (
                  <Link
                    key={row.id}
                    to={href}
                    className={className}
                    onClick={() => {
                      if (!row.read) void markRead([row.id]);
                      setOpen(false);
                    }}
                  >
                    {body}
                  </Link>
                ) : (
                  <button
                    key={row.id}
                    type="button"
                    className={className}
                    onClick={() => !row.read && void markRead([row.id])}
                  >
                    {body}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
